import { useEffect, useRef, useState } from "react";

export interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  total: number;
}

interface UseCountdownOptions {
  updateInterval?: number;
  onComplete?: () => void;
}

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = MS_PER_SECOND * 60;
const MS_PER_HOUR = MS_PER_MINUTE * 60;
const MS_PER_DAY = MS_PER_HOUR * 24;

const calculateTimeLeft = (targetDate: Date): TimeLeft => {
  const now = new Date().getTime();
  const target = targetDate.getTime();
  const difference = target - now;

  if (difference <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      total: 0,
    };
  }

  const days = Math.floor(difference / MS_PER_DAY);
  const remainderAfterDays = difference % MS_PER_DAY;

  const hours = Math.floor(remainderAfterDays / MS_PER_HOUR);
  const remainderAfterHours = remainderAfterDays % MS_PER_HOUR;

  const minutes = Math.floor(remainderAfterHours / MS_PER_MINUTE);
  const remainderAfterMinutes = remainderAfterHours % MS_PER_MINUTE;

  const seconds = Math.floor(remainderAfterMinutes / MS_PER_SECOND);

  return {
    days,
    hours,
    minutes,
    seconds,
    total: difference,
  };
};

export const useCountdown = (
  targetDate: Date | null,
  options?: UseCountdownOptions,
): TimeLeft | null => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(() =>
    targetDate ? calculateTimeLeft(targetDate) : null,
  );
  const intervalRef = useRef<number | null>(null);

  const onCompleteRef = useRef<(() => void) | undefined>(undefined);
  useEffect(() => {
    onCompleteRef.current = options?.onComplete;
  }, [options?.onComplete]);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!targetDate) {
      setTimeLeft(null);
      return;
    }

    const initial = calculateTimeLeft(targetDate);
    setTimeLeft(initial);

    const intervalMs = options?.updateInterval ?? 1000;
    if (initial.total <= 0) {
      onCompleteRef.current?.();
      return;
    }

    intervalRef.current = window.setInterval(() => {
      const next = calculateTimeLeft(targetDate);

      setTimeLeft(next);

      if (next.total <= 0) {
        if (intervalRef.current !== null) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }

        onCompleteRef.current?.();
      }
    }, intervalMs);

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [targetDate, options?.updateInterval]);

  return timeLeft;
};
